/**
 * The `ask_user` question card's contents, read from the interrupt's action request.
 *
 * The agent's plan-mode `ask_user` tool (`src/agent/src/agent/plan_mode_tools.ts`) sends a
 * `question`, optionally 2–5 `options`, and `allowMultiple`. The HITL middleware also stamps
 * every action with a `description` that is the tool's boilerplate followed by
 * `Tool: ask_user` and `Args: {…}` — the serialized arguments. The first card used that
 * description as the question, so the user read the raw payload and never saw the choices
 * (#99). The rules here are the web client's (`bimgo-ai-web` `Message.tsx`, the `isAskUser`
 * branch), so the two renderers answer the same interrupt the same way:
 *
 *   - the question is `args.question`, never the middleware description;
 *   - every string in `args.options` is a selectable choice;
 *   - `allowMultiple: true` is multi-select, anything else single-select;
 *   - a free-text "other" is always offered, and the answer is the selected options
 *     followed by the free text, joined with `; `.
 */
import type { ActionRequest } from "./chat";

export interface AskUserPrompt {
  /** The agent's question. Empty when the action carries none. */
  question: string;
  /** The preset choices, in the agent's order, blank and duplicate entries dropped. */
  options: string[];
  /** Whether more than one choice may be selected at once. */
  allowMultiple: boolean;
}

/** The middleware's `Tool:` / `Args:` boilerplate — never a question. */
const MIDDLEWARE_BOILERPLATE = /^(Tool|Args):\s/m;

export function describeAskUser(action: ActionRequest): AskUserPrompt {
  const args = action.args ?? {};
  const fromArgs = typeof args["question"] === "string" ? args["question"].trim() : "";
  // A description that is not the middleware's own text is an acceptable fallback for an
  // agent that put the question there — the old contract — but the boilerplate is not.
  const fromDescription =
    !fromArgs && typeof action.description === "string" && !MIDDLEWARE_BOILERPLATE.test(action.description)
      ? action.description.trim()
      : "";
  const options: string[] = [];
  if (Array.isArray(args["options"])) {
    for (const option of args["options"]) {
      if (typeof option !== "string") continue;
      const text = option.trim();
      if (text && !options.includes(text)) options.push(text);
    }
  }
  return {
    question: fromArgs || fromDescription,
    options,
    allowMultiple: args["allowMultiple"] === true,
  };
}

/**
 * The selection after clicking one option: multi-select toggles it in and out, single-select
 * replaces the selection (and clicking the selected option clears it, so a user can go back to
 * free text alone).
 */
export function toggleAskOption(
  selected: readonly string[],
  option: string,
  allowMultiple: boolean,
): string[] {
  const has = selected.includes(option);
  if (allowMultiple) return has ? selected.filter((o) => o !== option) : [...selected, option];
  return has ? [] : [option];
}

/** The `resume.answer` the agent receives: selected options first, then the free text. */
export function composeAskAnswer(selected: readonly string[], other: string): string {
  const parts = [...selected];
  const extra = other.trim();
  if (extra) parts.push(extra);
  return parts.join("; ");
}
